import { HomeIconLine } from '@/assets/icons';
import { useRecentViewExpericeProducts } from '@/features/Home/hooks/useRecentViewExpericeProducts';
import { useFavoriteStore } from '@/store/useFavoriteStore';
import { useViewportStore } from '@/store/useViewportStore';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import Typography from '../common/Typography';
import HeartButton from '../common/button/HeartButton';

const ExperienceProductCard = ({
  id,
  name,
  location,
  categoryId,
  categoryName,
  villageName,
  price,
  originPrice,
  thumbnailUrl,
  productType,
  longitude,
  latitude,
  isWishlistPage = false,
}: ExperienceProduct & { isWishlistPage?: boolean }) => {
  const router = useRouter();
  const isMobile = useViewportStore((state) => state.isMobile);
  // 마을명 클릭 활성 조건: 트립 검색결과(/search)·마을 페이지(/category/village)에서만.
  const canVillageClick =
    !!villageName && (router.pathname === '/search' || router.pathname === '/category/village');
  const favoriteFromStore = useFavoriteStore((state) => state.isFavoriteProduct(Number(id)));
  const isFavorite = isWishlistPage ? true : favoriteFromStore;
  const { setRecentViewExpericeProducts } = useRecentViewExpericeProducts();

  const discountRate =
    originPrice && originPrice !== 0 ? Math.floor((1 - price / originPrice) * 100) : 0;

  const handleClick = () => {
    setRecentViewExpericeProducts({
      id,
      name,
      location,
      categoryId,
      categoryName,
      villageName,
      price,
      originPrice,
      thumbnailUrl,
      productType,
      longitude,
      latitude,
    });

    router.push(`/product/${id}`);
  };

  return (
    <CardContainer onClick={handleClick}>
      <ImageWrapper>
        <ProductImage src={thumbnailUrl} alt={name} />
        <HeartButton productId={Number(id)} isFavorite={isFavorite} productType="tour" />
      </ImageWrapper>
      <CardContent>
        <div
          className="source"
          onClick={(e) => {
            // 마을명 클릭 → 마을 페이지에서 그 마을 상품만 보여준다(마켓 마을 브라우징과 동일 방식).
            if (!canVillageClick) return;
            e.stopPropagation();
            router.push(`/category/village?villageName=${encodeURIComponent(villageName)}`);
          }}
          style={{ cursor: canVillageClick ? 'pointer' : 'default' }}>
          {!isMobile && <HomeIconLine width={12} height={12} />}
          <Typography variant={isMobile ? 'verySmall' : 'h4b'} color="gray800">
            {villageName}
          </Typography>
        </div>

        <Typography ellipsisLines={2} variant="b1" weight="semibold">
          {name}
        </Typography>
        <PriceInfo>
          <Discount>
            {discountRate > 0 && (
              <Typography variant="h3" color="point2">
                {discountRate}%
              </Typography>
            )}
            <Typography variant="h3">{price.toLocaleString()}원</Typography>
          </Discount>
          {originPrice && discountRate > 0 && (
            <OriginalPrice>
              <Typography variant="b3" color="gray600">
                {originPrice.toLocaleString()}원
              </Typography>
            </OriginalPrice>
          )}
        </PriceInfo>
        {/* <div className="rating">
          <StarIcon width={12} height={12} />
          <Typography variant="b3" color="gray900">
            {reviewRate}
          </Typography>
        </div> */}
      </CardContent>
    </CardContainer>
  );
};

export default ExperienceProductCard;

const CardContainer = styled.div<{ disabled?: boolean }>`
  position: relative;
  width: 100%;
  max-width: 232px; /* 기본 PC 버전 크기 */
  overflow: hidden;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  @media (max-width: 768px) {
    max-width: 200px; /* 태블릿에서 조금 작게 */
  }

  @media (max-width: 480px) {
    max-width: 180px; /* 모바일에서는 더 작게 */
  }
  @media (max-width: 375px) {
    max-width: 130px;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1; /* 가로 3 : 세로 4 비율 유지 */
  overflow: hidden;
  border-radius: 6px;

  @media (max-width: 768px) {
    aspect-ratio: 1 / 1;
  }

  @media (max-width: 480px) {
    aspect-ratio: 1 / 1; /* 모바일에서도 같은 비율 유지 */
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.1s ease-in;

  &:hover {
    transform: scale(1.03);
  }
`;

const SoldOutOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const CardContent = styled.div`
  margin-top: 13px;
  .source {
    display: flex;
    gap: 4px;
    align-items: center;
    :first-child {
      transform: translateY(-2px);
    }
  }
  .rating {
    display: flex;
    gap: 4px;
    align-items: center;
  }
`;

const Discount = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
const PriceInfo = styled.div`
  display: flex;
  align-items: center;

  gap: 4px;
  margin: 2px 0;
  flex-wrap: wrap;
`;

const OriginalPrice = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray600};
  white-space: nowrap;
  text-decoration: line-through;

  @media (max-width: 480px) {
    margin-top: 2px;
  }
`;
